import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import { APPROVAL_V2_URL } from '@/constant/approval';
import { postDataFetcher } from '@/utils/apis';

export type UseRequestReturnApprovalOptions = {
  onSuccess?: (response: Record<string, unknown>) => void;
  onError?: (error: unknown) => void;
};

/**
 * 기안자의 반려요청 mutation.
 * - POST /api/v2/approval/{documentId}/return-request
 * - 회수와 달리 문서 상태는 그대로 진행중(P)/보류(H)다. 요청 표식과 사유만 남고,
 *   현재 결재자에게 알림이 발송된다(발송은 서버 소관).
 * - 재요청하면 서버가 이전 사유를 덮어쓴다(레거시 동작).
 * - 권한: 기안자 본인만. 본인이 아니면 서버가 403.
 * - 응답 코드:
 *   204 요청 성공
 *   403 기안자가 아님
 *   404 문서를 찾을 수 없음
 *   422 요청 불가 — 완료·반려·취소·임시저장 상태이거나, 아무도 결재하지 않아 회수 대상인 경우
 */
const useRequestReturnApproval = (
  options?: UseRequestReturnApprovalOptions,
) => {
  const { mutate } = useSWRConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestReturn = useCallback(
    async (
      documentId: string,
      reason: string,
    ): Promise<Record<string, unknown>> => {
      setIsSubmitting(true);
      const baseKey = `${APPROVAL_V2_URL}/${documentId}`;
      try {
        const response = await postDataFetcher<
          Record<string, unknown>,
          Record<string, unknown>
        >(`${baseKey}/return-request`, { reason });
        // 단건 캐시 무효화 + 재검증 — 문자열 key(useApproval) + 튜플 key(useApprovalView) 동시 매칭.
        // 요청 표식·사유가 응답에 반영돼야 조회 화면이 안내 박스를 띄운다.
        await mutate(
          (cacheKey) =>
            cacheKey === baseKey ||
            (Array.isArray(cacheKey) && cacheKey[0] === baseKey),
        );
        options?.onSuccess?.(response);
        return response;
      } catch (error) {
        options?.onError?.(error);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [mutate, options],
  );

  return { requestReturn, isSubmitting };
};

export default useRequestReturnApproval;
